// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Libs
import { DateTime } from 'luxon';
// Actions & store
import actionsEnvelopes from '../../../api/actionsEnvelopes';
import useEnvelopesStore from '@/app/store/modules/envelopes';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
// Components
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import CreateEnvelopeForm from './components/CreateEnvelopeForm';
import FooterClose from '@/common-utils/components/renders/modal/components/footers/FooterClose/FooterClose';
import Entity from '@/common-utils/components/renders/entity/Entity';
import SubscriptionsTable from '@/app/modules/subscriptions/components/tables/SubscriptionsTable';
import OrganizationsTable from '@/app/modules/organizations/components/tables/OrganizationsTable';
// Common
import organizationFields from '@/app/modules/organizations/fields/organizationFields';
import envelopesFields from '../../../fields/envelopesFields';
import subscriptionFields from '@/app/modules/subscriptions/fields/subscriptionFields';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
import ORGANIZATION_TYPES from '@/app/modules/organizations/enums/OrganizationTypes';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
// Lang
import { nsEnvelope, nsErrors } from '@/app/i18n/namespaces';

/**
 * @param {function} setIsOpen
 * @returns {JSX}
*/
function ModalCreateEnvelopeByForm({ isOpen, setIsOpen }) {
    // Constants
    const STEPS_NUMBER = 3;
    const STEP_TITLE_PREFIX = 'modal.create_envelopes.steps';

    // Store
    const storeRefetchEnvelopes = useEnvelopesStore(s => s.storeRefetchEnvelopes);

    // Toast
    const { toast } = useToast();

    // State
    const [step, setStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [isValid, setIsValid] = useState(false);
    const [errorMessage, setErrorMessage] = useState();

    // Tables selection
    const [clientSelected, setClientSelected] = useState(getInitialValueForRowSelected());
    const [posTerminalsSelected, setPosTerminalsSelected] = useState(getInitialValueForRowSelected());

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        storeRefetchEnvelopes(true);
        toast({
            description: <Entity namespace={nsEnvelope} entity='modal.create_envelopes.success' />,
            variant: 'success'
        });
    };
    const onError = (error) => {
        setErrorMessage(error.response?.data?.errorCode ?? error.code ?? 'UNKNOWN_ERROR');
        setStep(step + 1);
    };

    const createEnvelope = actionsEnvelopes.createEnvelope(onSuccess, onError);

    // Hooks
    const { stepTitle, disabledNext, modalSize } = useStepConfig(
        step,
        () => ({
            0: {
                stepTitle: `${STEP_TITLE_PREFIX}.client_selection`,
                disabledNext: !Object.keys(clientSelected.rowIds).length,
                modalSize: 'max-w-4xl'
            },
            1: {
                stepTitle: `${STEP_TITLE_PREFIX}.pos_terminal_selection`,
                disabledNext: !Object.keys(posTerminalsSelected.rowIds).length,
                modalSize: 'max-w-4xl'
            },
            2: {
                stepTitle: `${STEP_TITLE_PREFIX}.form_fill`,
                disabledNext: false,
                modalSize: 'max-w-2xl'
            },
            3: {
                stepTitle: '',
                disabledNext: false,
                modalSize: 'max-w-xl'
            }
        }),
        [clientSelected, posTerminalsSelected]
    );

    // Functions
    const onFormSubmit = () => {
        const { scheduledStartDate, scheduledEndDate, accessCode, ipServer } = formData;
        const postTerminalsUuids = posTerminalsSelected.rowsData.map((posTerminal) =>
            getObjectAttributeFromStringKey(posTerminal, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.UUID))
        );

        const envelopeDataToSend = {
            [envelopesFields.ORGANIZATION_UUID.key]: clientSelected.rowIds && Object.keys(clientSelected.rowIds)[0],
            [envelopesFields.POS_TERMINAL_UUIDS.key]: postTerminalsUuids,
            [envelopesFields.SCHEDULED_START_DATE.key]: DateTime.fromJSDate(scheduledStartDate).toISO(),
            [envelopesFields.SCHEDULED_END_DATE.key]: DateTime.fromJSDate(scheduledEndDate).toISO(),
            [envelopesFields.ACCESS_CODE.key]: accessCode,
            [envelopesFields.DOWNLOAD_SERVER_IP_ADDRESS_PORT.key]: ipServer
        };

        createEnvelope.mutate({ envelopeDataToSend });
    };

    // Constants
    const clientName = clientSelected.rowsData[0]?.[organizationFields.LEGAL_NAME.key];

    // Render
    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={modalSize}>
                <HeaderTitle
                    title={'modal.create_envelopes.title'}
                    namespace={nsEnvelope}
                />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsEnvelope}
                        className={'mb-2 flex'}
                    />
                    {step === 0 &&
                        <OrganizationsTable
                            filtersEventName={setFiltersEventName('organizations', true)}
                            customQuery={{
                                filters: [
                                    {
                                        key: organizationFields.TYPE.key,
                                        value: ORGANIZATION_TYPES.WHITE_LABEL.name,
                                        hidden: true
                                    }
                                ]
                            }}
                            singleSelection={true}
                            rowsSelected={clientSelected}
                            setRowsSelected={setClientSelected}
                        />
                    }
                    {step === 1 &&
                        <SubscriptionsTable
                            filtersEventName={setFiltersEventName('subscriptions', true)}
                            customQuery={{
                                filters: [
                                    {
                                        key: generateNestedFieldAccessor(subscriptionFields.CLIENT.UUID),
                                        value: clientSelected.rowIds && Object.keys(clientSelected.rowIds)[0],
                                        hidden: true
                                    }
                                ]
                            }}
                            rowsSelected={posTerminalsSelected}
                            setRowsSelected={setPosTerminalsSelected}
                        />
                    }
                    {step === 2 &&
                        <CreateEnvelopeForm
                            client={clientName}
                            posTerminals={posTerminalsSelected.rowsData}
                            setFormData={setFormData}
                            setFormValidation={setIsValid}
                            onFormSubmit={onFormSubmit}
                        />
                    }
                    {step === 3 &&
                        <div className='text-center'>
                            {
                                errorMessage ?
                                    <Entity namespace={nsErrors} entity={`api.envelopes.creation.${errorMessage}`} />
                                    :
                                    <Entity namespace={nsErrors} entity='api.envelopes.creation.INTERNAL_SERVER_ERROR' />
                            }
                        </div>
                    }
                </ModalBody>
                {step < 3 &&
                    <FooterStep
                        step={step}
                        setStep={setStep}
                        stepsNumber={STEPS_NUMBER}
                        onValidate={onFormSubmit}
                        isLoading={createEnvelope.isLoading}
                        disableValidation={disabledNext || step === 2 && !isValid}
                    />
                }
                {step === 3 && <FooterClose />}
            </ModalContent>
        </Modal >
    );
}

ModalCreateEnvelopeByForm.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired
};

export default ModalCreateEnvelopeByForm;